import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, slotLabel } from "@/api";
import { TrendingUp, ClipboardList } from "lucide-react";

const dot = (s) => ({
  completed: "bg-emerald-400",
  confirmed: "bg-[#00e5ff]",
  cancelled: "bg-[#ff3b30]",
}[s] || "bg-zinc-600");

export default function Progress() {
  const [athletes, setAthletes] = useState([]);
  useEffect(() => { api.get("/progress/mine").then((r) => setAthletes(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="progress-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
      <span className="text-[11px] uppercase tracking-[0.3em] text-[#00e5ff]">Development</span>
      <h1 className="display text-4xl sm:text-5xl uppercase tracking-tighter font-bold mt-3">Progress</h1>
      <p className="text-sm text-zinc-400 mt-4 max-w-xl leading-relaxed">Every session your goalie has taken, with the notes and drills their coaches left behind.</p>

      {athletes.length === 0 ? (
        <div data-testid="no-progress" className="mt-12 border border-white/10 p-12 text-center">
          <p className="text-sm text-zinc-400">Nothing tracked yet — book a session to start a development log.</p>
          <Link to="/coaches" className="inline-block mt-6 bg-[#00e5ff] text-black text-xs font-bold uppercase px-6 py-3 hover:bg-[#00b3cc] transition-colors duration-200">Browse coaches</Link>
        </div>
      ) : (
        <div className="mt-12 space-y-12">
          {athletes.map((a) => (
            <section key={a.athlete_name} data-testid={`athlete-${a.athlete_name.replace(/\s+/g, "-")}`}>
              <div className="flex flex-wrap items-end justify-between gap-4 border-b border-white/10 pb-4">
                <div>
                  <h2 className="display text-2xl sm:text-3xl uppercase tracking-tight font-bold">{a.athlete_name}</h2>
                  <p className="text-[11px] text-zinc-500 mt-1">{a.disciplines.join(" · ") || "No disciplines yet"}</p>
                </div>
                <div className="flex gap-8">
                  <div>
                    <p className="display text-3xl font-bold text-[#00e5ff] leading-none">{a.sessions.length}</p>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Sessions</p>
                  </div>
                  <div>
                    <p className="display text-3xl font-bold leading-none">{a.completed}</p>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Completed</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                {a.sessions.map((s) => (
                  <div key={s.id} data-testid={`progress-session-${s.id}`} className="border border-white/10 bg-[#121212] p-5">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className={`w-2 h-2 rounded-full ${dot(s.status)}`} />
                      <p className="display uppercase font-bold">{s.coach_name}</p>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500 border border-white/10 px-2 py-1">{s.category}</span>
                      <span className="text-[11px] text-zinc-500 ml-auto">{s.slot ? slotLabel(s.slot) : "Remote"}</span>
                    </div>
                    <p className="text-xs text-zinc-400 mt-3">{s.service_title}</p>
                    {s.coach_note && (
                      <p className="text-sm text-zinc-200 mt-3 flex gap-2 leading-relaxed">
                        <TrendingUp className="w-4 h-4 text-[#00e5ff] shrink-0 mt-0.5" /> {s.coach_note}
                      </p>
                    )}
                    {s.drills?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Drills to work on</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {s.drills.map((d) => (
                            <span key={d} className="text-[11px] border border-[#00e5ff]/40 text-[#00e5ff] px-2.5 py-1">{d}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
